<template>
    <div class="site-header" :class="{ scrolled }">
        <div class="site-header-inner">
            <div class="left-area">
                <div class="brand" @click="router.push({ path: '/' })">
                    <div class="brand-icon">
                        <img src="@/assets/images/index/headerLogo.png" alt="" />
                    </div>
                    <div class="brand-title">{{ $t('header.console') }}</div>
                </div>
                <nav class="nav">
                    <div :class="['nav-item', { active: activeNav === 'model' }]" @click="toDocument('model')">
                        {{ $t('header.model') }}
                    </div>
                    <div :class="['nav-item', { active: activeNav === 'docs' }]" @click="toDocument('docs')">
                        {{ $t('header.docs') }}
                    </div>
                    <div :class="['nav-item', { active: activeNav === 'about' }]" @click="toDocument('about')">
                        {{ $t('header.about') }}
                    </div>
                </nav>
            </div>
            <div class="right-area">
                <div class="quick-nav">
                    <i18n-selector>
                        <span class="nav-item lang-item">
                            <i class="ri-global-line" />
                            <span class="lang-text">{{ currentLangLabel }}</span>
                            <ElIconArrowDown class="arrow" />
                        </span>
                    </i18n-selector>
                </div>
                <!-- 未登录：显示登录/注册按钮 -->
                <template v-if="!isLogin">
                    <button class="auth-btn-login" @click="router.push({ name: 'login' })">{{ $t('header.login') || '登录'
                        }}</button>
                    <button class="auth-btn-register" @click="router.push({ name: 'login' })">{{ $t('header.register')
                        || '注册' }}</button>
                </template>
                <!-- 已登录：显示用户信息 -->
                <template v-else>
                    <span class="user-wrap" style="cursor: pointer" @click="router.push({ path: '/accountManage' })">
                        <el-avatar :size="30" :src="avatar" v-if="avatar" />
                        <el-avatar :size="30" v-else>{{ initials }}</el-avatar>
                        <span class="username">{{ loginUser }}</span>
                    </span>
                </template>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import storage from '@/util/storage';
const router = useRouter();
const route = useRoute();
const store = useStore();
const { t } = useI18n();

const isLogin = computed(() => store.getters['user/isLogin']);
const avatar = ref(''); // 可从用户信息填充图片地址
const scrolled = ref(false);
const userInfo = computed(() => store.state.user.userData || {});

const currentLangLabel = computed(() => {
    const lang = store.state.settings.defaultLang || 'en';
    const map = { 'zh-cn': '简体中文', 'zh-tw': '繁體中文', 'en': 'English' };
    return map[lang] || 'English';
});

const activeNav = computed(() => {
    const path = route.path || '';

    // 如果路由设置了 activeMenu，优先使用 activeMenu
    if (route.meta && route.meta.activeMenu) {
        if (route.meta.activeMenu === '/accountManage') {
            return 'model';
        }
    }

    if (path.startsWith('/accountManage')) {
        return 'model';
    }

    if (path.startsWith('/about')) {
        return 'about';
    }

    return '';
});

// 登录用户显示
const loginUser = computed(() => {
    return (
        store.state.user.realName ||
        userInfo.value.userName ||
        storage.local.get('login_user') ||
        t('header.userDefault')
    );
});

const initials = computed(() => {
    const name = loginUser.value || '';
    return name.slice(0, 1);
});

function toDocument (key) {
    if (key === 'model') {
        router.push({ path: '/accountManage/modelSquare' });
    } else if (key === 'docs') {
        const lang = store.state.settings.defaultLang || 'en';
        const docsUrl = lang === 'en' ? 'https://docs.krestaai.com.au/en/' : 'https://docs.krestaai.com.au/';
        window.open(docsUrl, '_blank');
    } else if (key === 'about') {
        router.push({ path: '/about' });
    }
}

function handleScroll () {
    scrolled.value = window.scrollY > 20;
}

onMounted(() => {
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
});

onBeforeUnmount(() => {
    window.removeEventListener('scroll', handleScroll);
});
</script>

<style scoped lang="scss">
.site-header {
    width: 100%;
    background: transparent;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    box-shadow: none;
    position: fixed;
    top: 0;
    border-bottom: none;
    z-index: 1000;
    transition: background 0.3s, box-shadow 0.3s, border-color 0.3s, backdrop-filter 0.3s;

    &.scrolled {
        background: rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(4px);
        -webkit-backdrop-filter: blur(2px);
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    }

    .site-header-inner {
        width: 100%;
        max-width: 1280px;
        margin: 0 auto;
        height: 66px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 24px;
        box-sizing: border-box;
    }

    .left-area {
        display: flex;
        align-items: center;
        gap: 32px;
        min-width: 0;

        .brand {
            display: flex;
            align-items: center;
            gap: 12px;
            cursor: pointer;

            .brand-icon {
                width: 30px;
                height: 30px;

                border-radius: 8px;
                box-shadow: 0 3px 8px rgba(91, 75, 255, 0.12);

                img {
                    display: block;
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
            }

            .brand-title {
                font-weight: 700;
                font-size: 16px;
                color: #222;
                font-family: 'Microsoft YaHei', 'PingFang SC', -apple-system, sans-serif;
            }
        }

        .nav {
            display: flex;
            align-items: center;
            gap: 24px;

            .nav-item {
                font-size: 16px;
                font-weight: bold;
                color: #222;
                cursor: pointer;
                padding: 6px 8px;
                border-radius: 6px;

                &:hover {
                    color: #155ded;
                }

                &.active {
                    color: #155ded;
                    font-weight: 600;
                }
            }

            .fee-dropdown .fee-label {
                display: inline-flex;
                align-items: center;
                gap: 6px;
                // color: #155ded;
            }

            .arrow {
                font-size: 12px;
                width: 18px;
                height: 18px;
            }

            .fee-dropdown.active .fee-label {
                color: #155ded;
            }

            .fee-dropdown.active .arrow {
                color: #155ded;
            }
        }
    }

    .right-area {
        display: flex;
        align-items: center;
        gap: 16px;
        flex-shrink: 0;

        .auth-btn-login,
        .auth-btn-register {
            padding: 6px 18px;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            border: none;
            transition: all 0.2s;
        }

        .auth-btn-login {
            background: transparent;
            color: #222;
            border: 1px solid #ddd;

            &:hover {
                color: #155ded;
                border-color: #155ded;
            }
        }

        .auth-btn-register {
            background: #155ded;
            color: #fff;

            &:hover {
                background: #1048c9;
            }
        }

        .nav-item {
            font-size: 14px;
            color: #222;
            cursor: pointer;
            padding: 6px 8px;
            border-radius: 6px;

            &:hover {
                color: #155ded;
            }

            &.active {
                color: #155ded;
                font-weight: 600;
            }
        }

        .lang-item {
            font-size: 14px;
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 6px 10px;
            border-radius: 6px;
            cursor: pointer;
            color: #222;
            white-space: nowrap;

            &:hover {
                color: #155ded;
            }

            .ri-global-line {
                font-size: 18px;
            }

            .lang-text {
                font-size: 14px;
            }

            .arrow {
                font-size: 12px;
                width: 16px;
                height: 16px;
            }
        }

        .fee-dropdown .fee-label {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            // color: #155ded;
        }

        .arrow {
            font-size: 12px;
            width: 18px;
            height: 18px;
        }

        .fee-dropdown.active .fee-label {
            color: #155ded;
        }

        .fee-dropdown.active .arrow {
            color: #155ded;
        }

        .quick-nav {
            display: flex;
            align-items: center;
            gap: 20px;

            .nav-item {
                font-size: 14px;
                color: #222;
                cursor: pointer;
                padding: 6px 8px;
                border-radius: 6px;

                &:hover {
                    color: #155ded;
                }

                &.active {
                    color: #155ded;
                    font-weight: 600;
                }
            }

            .fee-dropdown .fee-label {
                display: inline-flex;
                align-items: center;
                gap: 6px;
            }

            .fee-dropdown.active .fee-label,
            .fee-dropdown.active .arrow {
                color: #155ded;
            }
        }

        .icon-btn {
            padding: 6px;
            border-radius: 6px;
            color: #2b2b2b;
        }

        .bell-badge {
            vertical-align: middle;
            transform: translateY(5px);
            margin-right: 10px;
            cursor: pointer;
        }

        .bell-icon {
            width: 20px;
            height: 20px;
            color: #222;
        }

        .user-wrap {
            display: inline-flex;
            align-items: center;
            gap: 10px;
            padding: 0;
        }

        .username {
            font-weight: 500;
            color: #222;
            margin-right: 4px;
        }

        .caret {
            font-size: 12px;
            color: #999;
            width: 18px;
            height: 18px;
        }
    }
}

:deep(.el-dropdown-menu) {
    padding: 0 !important;
}

// el-dropdown-menu el-dropdown-menu--default user-dropdown
:deep(.el-dropdown-menu--default) {
    padding: 0 !important;
}

/* 响应式 */
@media (max-width: 1000px) {
    .site-header .site-header-inner {
        padding: 0 12px;
    }

    .left-area {
        gap: 12px;
    }

    .nav {
        display: none;
    }

    .quick-nav {
        display: none !important;
    }
}

.arrow,
.caret {
    font-size: 2px;
    color: #999;
    vertical-align: middle;
}

:deep(.el-dropdown-menu) {
    background: #fff !important;
    border-radius: 12px !important;
    box-shadow: 0 8px 30px rgba(34, 52, 84, 0.12) !important;
    min-width: 320px;
    padding: 0 !important;
    border: 1px solid #eef1f6;
    overflow: hidden;
}

:deep(.el-dropdown-menu__item) {
    height: 46px;
    padding: 0 28px;
    margin: 0;
    border-radius: 0;
    transition:
        background 0.2s,
        color 0.2s;
    line-height: 46px;
    font-weight: 400;
    font-size: 16px;
    color: #3b4663;
}

:deep(.el-dropdown-menu__item--divided) {
    margin: 0 !important;
    border-top: 1px solid #e9edf5;
}

:deep(.el-dropdown-menu__item--divided::before) {
    display: none !important;
    height: 0 !important;
    margin: 0 !important;
}

:deep(.el-dropdown-menu__item:not(.is-disabled):hover) {
    background: #fff !important;
    color: #3b4663 !important;
}

:deep(.el-dropdown-menu__item:not(.is-disabled):focus) {
    background: #fff !important;
    color: #3b4663 !important;
}

.user-dropdown {
    width: 320px;

    padding: 0 !important;

    :deep(.el-dropdown-menu__item:last-child) {
        justify-content: center;
        color: #ff5a45 !important;
        font-weight: 500;
        border-top: 1px solid #e9edf5;
    }

    :deep(.el-dropdown-menu__item:last-child:hover) {
        background: #fff5f2 !important;
        color: #ff5a45 !important;
    }

    :deep(.el-dropdown-menu__item:last-child:focus) {
        background: #fff !important;
        color: #ff5a45 !important;
    }
}

.switch-account-trigger {
    border-top: 1px solid #e9edf5;
}

.switch-account-header {
    font-size: 16px;
    font-weight: 600;
    color: #273142;
    margin-bottom: 12px;
}

.switch-account-status {
    padding: 28px 0;
    text-align: center;
    font-size: 14px;
    color: #65708a;
}

.switch-account-status.empty {
    color: #98a2b3;
}

.switch-account-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 320px;
    overflow-y: auto;
}

.switch-account-item {
    width: 100%;
    display: flex;
    align-items: center;
    padding: 10px 12px;
    border: 1px solid transparent;
    border-radius: 8px;
    background: #fff;
    text-align: left;
    cursor: pointer;
    transition:
        border-color 0.2s,
        background 0.2s,
        color 0.2s;
}

.switch-account-item:hover {
    background: #f7f9fc;
}

.switch-account-item:disabled {
    cursor: default;
}

.switch-account-item.active {
    background: #f1f4fa;
    border-color: #e3e8f2;
}

.switch-account-item.loading {
    opacity: 0.75;
}

.switch-account-item-main {
    min-width: 0;
    flex: 1;
    display: flex;
    align-items: center;
    gap: 12px;
}

.switch-account-tag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    min-width: 48px;
    height: 30px;
    padding: 0 14px;
    border-radius: 999px;
    border: 1px solid #d6dce7;
    background: #fafbfd;
    color: #4b556b;
    font-size: 14px;
    line-height: 1;
    box-sizing: border-box;
}

.switch-account-name {
    min-width: 0;
    flex: 1;
    font-size: 15px;
    line-height: 22px;
    font-weight: 500;
    color: #273142;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

:deep(.el-dropdown-menu__item .el-dropdown-menu__item) {
    /* 防止嵌套时样式错乱 */
    margin: 0;
    padding: 0;
}

:deep(.dropdown-account-info) {
    padding: 20px;
    margin-bottom: 0;
    background: linear-gradient(180deg, #f7f9ff 0%, #eef3ff 100%);
    border-bottom: 1px solid #e9edf5;
}

:deep(.account-name) {
    font-size: 18px;
    line-height: 26px;
    font-weight: 600;
    color: #273142;
    margin-bottom: 5px;
    word-break: break-all;
}

:deep(.account-tag-row) {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 12px;
}

:deep(.account-tag) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 22px;
    padding: 0 8px;
    border: 1px solid #d9e0ef;
    border-radius: 6px;
    background: #fff;
    font-size: 14px;
    color: #4a5570;
    box-sizing: border-box;
}

:deep(.auth-btn) {
    height: 32px;
    padding: 0 14px;
    border: 1px solid #ffb08a;
    border-radius: 6px;
    background: #fff7f3;
    color: #ff7a45;
    font-size: 14px;
    cursor: pointer;
    box-sizing: border-box;
}

:deep(.auth-btn:hover) {
    background: #fff1ea;
}

:deep(.el-dropdown-menu),
:deep(.el-dropdown-menu__item),
:deep(.el-button),
:deep(.el-avatar),
:deep(.el-dropdown) {
    outline: none !important;
    box-shadow: none !important;
}

:deep(.el-dropdown-menu__item:focus),
:deep(.el-button:focus),
:deep(.el-avatar:focus),
:deep(.el-dropdown:focus) {
    outline: none !important;
    box-shadow: none !important;
}

:deep(:focus-visible) {
    outline: none !important;
}

/* 隐藏下拉内部滚动条，但仍允许滚动（兼容 WebKit/Firefox/IE） */
:deep(.el-dropdown-menu),
:deep(.el-dropdown-menu *) {
    -ms-overflow-style: none;
    /* IE 10+ */
    scrollbar-width: none;
    /* Firefox */
}

:deep(.el-dropdown-menu)::-webkit-scrollbar,
:deep(.el-dropdown-menu *)::-webkit-scrollbar {
    width: 0px;
    height: 0px;
}
</style>
